import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../context/ModeContext';
import { toast } from 'sonner';
import { getWorkerIcon } from '../utils/workerIcons';
import { toListWorker, groupWorkers, WORKER_TYPE_LABELS } from '../utils/workerCompat';
import NewWorkerModal from '../components/NewWorkerModal';
import UniversalVoiceWizard from '../components/UniversalVoiceWizard';
import { CreateOperatorModal } from './Operators';
import {
  MagnifyingGlass,
  Plus,
  Robot,
  Lightning,
  Eye,
  Clock,
  CircleNotch,
  ArrowRight,
  ListDashes,
  UsersThree,
  UserCircle,
  Bell,
  CheckCircle,
  Warning,
  Trash,
  ShieldCheck,
  Wrench,
  HandPalm,
  HourglassMedium,
  PlugsConnected,
  Plugs,
  Pulse,
  TreeStructure,
  Package,
} from '@phosphor-icons/react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';

/* ─── Outcome-focused helpers ─── */
const OUTCOME_MAP = {
  'customer enquiry': 'Captures and responds to new enquiries instantly',
  'file': 'Analyzes uploaded files and delivers insights',
  'analysis': 'Analyzes uploaded files and delivers insights',
  'appointment': 'Sends timely reminders for upcoming appointments',
  'reminder': 'Sends timely reminders for upcoming appointments',
  'never miss a lead': 'Captures every lead and keeps your pipeline full',
  'lead': 'Captures every lead and keeps your pipeline full',
  'follow-up': 'Follows up with customers to close the loop',
  'follow up': 'Follows up with customers to close the loop',
  'review': 'Collects reviews from happy customers automatically',
  'booking': 'Books appointments and confirms with customers',
  'invoice': 'Handles invoice queries and payment follow-ups',
  'payment': 'Handles invoice queries and payment follow-ups',
};

const getOutcomeStatement = (w) => {
  const lower = (w.name || '').toLowerCase();
  for (const [key, statement] of Object.entries(OUTCOME_MAP)) {
    if (lower.includes(key)) return statement;
  }
  if (w.steps && w.steps.length > 0) {
    return w.steps.map(s => s.label).slice(0, 2).join(', then ').toLowerCase().replace(/^./, c => c.toUpperCase());
  }
  return 'Handles tasks automatically for your business';
};

const getTriggerText = (w) => {
  const lower = (w.name || '').toLowerCase();
  const steps = w.steps || [];
  const firstStep = steps[0]?.label?.toLowerCase() || '';
  if (firstStep.includes('email') || firstStep.includes('gmail') || lower.includes('enquiry') || lower.includes('lead')) {
    return 'Runs when a new email arrives';
  }
  if (firstStep.includes('file') || firstStep.includes('upload') || lower.includes('file') || lower.includes('analysis')) {
    return 'Runs when a file is uploaded';
  }
  if (firstStep.includes('calendar') || lower.includes('appointment') || lower.includes('reminder')) {
    return 'Runs when an appointment is upcoming';
  }
  if (firstStep.includes('form') || firstStep.includes('submit')) {
    return 'Runs when a form is submitted';
  }
  return 'Runs automatically when triggered';
};

const getReviewLabel = (w) => {
  if (!w.requires_review) return { text: 'Auto', color: '#00E5C0', bg: 'rgba(0,229,192,0.08)' };
  if (w.status === 'active') return { text: 'Auto + Review', color: '#F59E0B', bg: 'rgba(245,158,11,0.06)' };
  return { text: 'Review required', color: '#F59E0B', bg: 'rgba(245,158,11,0.06)' };
};

const formatMetric = (w) => {
  const count = w.run_count || 0;
  if (count === 0) return 'No tasks yet';
  const label = count === 1 ? 'task handled' : 'tasks handled';
  return `${count} ${label}`;
};

const formatLastRun = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Last ran just now';
  if (mins < 60) return `Last ran ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Last ran ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `Last ran ${days}d ago`;
};

/* Deduplicate workers — keep the most active version of identically named workers */
const deduplicateWorkers = (workers) => {
  const map = new Map();
  for (const w of workers) {
    const key = w.name;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, w);
    } else {
      // Prefer active > draft, then higher run_count
      const existingScore = (existing.status === 'active' ? 1000 : 0) + (existing.run_count || 0);
      const newScore = (w.status === 'active' ? 1000 : 0) + (w.run_count || 0);
      if (newScore > existingScore) map.set(key, w);
    }
  }
  return Array.from(map.values());
};

/* ─── Trust classification badge config ─── */
const TRUST_BADGES = {
  ready: {
    label: 'Ready',
    color: '#00E5C0',
    bg: 'rgba(0,229,192,0.08)',
    border: 'rgba(0,229,192,0.15)',
    Icon: ShieldCheck,
  },
  needs_setup: {
    label: 'Needs setup',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.06)',
    border: 'rgba(245,158,11,0.12)',
    Icon: Wrench,
  },
  assist_only: {
    label: 'Assist only',
    color: '#8B8FF5',
    bg: 'rgba(139,143,245,0.06)',
    border: 'rgba(139,143,245,0.12)',
    Icon: HandPalm,
  },
  coming_soon: {
    label: 'Coming soon',
    color: '#7D8896',
    bg: 'rgba(255,255,255,0.02)',
    border: 'rgba(255,255,255,0.06)',
    Icon: HourglassMedium,
  },
};

const TrustBadge = ({ classification, reason }) => {
  const config = TRUST_BADGES[classification] || TRUST_BADGES.assist_only;
  const { Icon } = config;
  return (
    <div className="flex items-center gap-1.5 group/trust relative" data-testid={`trust-badge-${classification}`}>
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border"
        style={{ color: config.color, background: config.bg, borderColor: config.border }}
      >
        <Icon size={10} weight="bold" />
        {config.label}
      </span>
      {reason && (
        <div className="absolute bottom-full left-0 mb-1.5 hidden group-hover/trust:block z-30 w-60 pointer-events-none">
          <div className="bg-[#1A2030] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-[10px] text-[#B0B8C4] leading-relaxed shadow-xl">
            {reason}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   WORKER CARD — Outcome-driven, Apple-simple
   ═══════════════════════════════════════════════ */
const WorkerCard = ({ w, isOperate, navigate, variant = 'list', onDelete, trustData }) => {
  const isTeam = variant === 'team';
  const [confirming, setConfirming] = useState(false);
  const iconUrl = getWorkerIcon(w.outcome_category, w.name);
  const outcome = getOutcomeStatement(w);
  const trigger = getTriggerText(w);
  const reviewBadge = getReviewLabel(w);
  const metric = formatMetric(w);
  const lastRun = formatLastRun(w.last_run);

  return (
    <div
      data-testid={`worker-card-${w.id}`}
      className={`bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)] hover:border-[rgba(255,255,255,0.1)] hover:-translate-y-[3px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.45)] transition-all duration-200 group relative ${
        isTeam ? 'p-5 w-72 flex-shrink-0' : 'p-6'
      }`}
    >
      {/* Delete button — top-right, visible on hover */}
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
          data-testid={`delete-worker-${w.id}`}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(239,68,68,0.12)] text-[#7D8896] hover:text-[#EF4444] transition-all z-10"
          title="Delete worker"
        >
          <Trash size={14} />
        </button>
      )}

      {/* Confirm delete overlay */}
      {confirming && (
        <div
          className="absolute inset-0 bg-[#1E2836]/95 rounded-2xl flex flex-col items-center justify-center z-20 backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
          data-testid={`confirm-delete-${w.id}`}
        >
          <p className="text-sm text-[#F0F4F8] mb-1 font-medium">Delete {w.name}?</p>
          <p className="text-xs text-[#7D8896] mb-4">This cannot be undone.</p>
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
              className="px-3 py-1.5 text-xs text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              data-testid={`cancel-delete-${w.id}`}
            >
              Cancel
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(w.id, w.name); }}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[#EF4444] rounded-lg hover:bg-[#DC2626] transition-colors"
              data-testid={`confirm-delete-btn-${w.id}`}
            >
              Delete
            </button>
          </div>
        </div>
      )}

      <div onClick={() => navigate(`/workers/${w.id}/detail`)} className="cursor-pointer">
        {/* TOP: Icon + Name + Status */}
        <div className="flex items-start gap-3.5 mb-4">
          <div className="w-11 h-11 rounded-[10px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0 overflow-hidden">
            <img
              src={iconUrl}
              alt=""
              className="w-11 h-11 rounded-[10px] object-cover"
              loading="lazy"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors leading-tight" data-testid={`worker-name-${w.id}`}>
              {w.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[11px] font-medium ${w.status === 'active' ? 'text-[#4ADE80]' : 'text-[rgba(255,255,255,0.5)]'}`}>
                {w.status === 'active' ? 'Active' : 'Draft'}
              </span>
              {trustData && (
                <>
                  <span className="text-[#2A2F38]">·</span>
                  <TrustBadge classification={trustData.classification} reason={trustData.reason} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* MIDDLE: Outcome statement */}
        <p className="text-sm text-[#B0B8C4] leading-relaxed mb-1.5" data-testid={`worker-outcome-${w.id}`}>
          {outcome}
        </p>
        <p className="text-[11px] text-[#5C6778] mb-1.5" data-testid={`worker-trigger-${w.id}`}>
          {trigger}
        </p>
        {/* Tool stack line */}
        {(trustData?.tools?.length > 0 || w.tool_preferences?.length > 0) && (
          <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid={`worker-tools-${w.id}`}>
            <PlugsConnected size={10} className="text-[#5C6778] shrink-0" />
            <span className="text-[10px] text-[#5C6778]">
              Using: {(trustData?.tools || w.tool_preferences || []).slice(0, 3).join(', ')}{(trustData?.tools || w.tool_preferences || []).length > 3 ? ` +${(trustData?.tools || w.tool_preferences || []).length - 3}` : ''}
            </span>
          </div>
        )}
        {/* Tool connectivity transparency label */}
        {trustData?.classification === 'assist_only' && (
          <div className="flex items-center gap-1.5 mb-3" data-testid={`worker-draft-label-${w.id}`}>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border bg-[rgba(139,143,245,0.04)] border-[rgba(139,143,245,0.12)] text-[#8B8FF5]">
              Draft behavior
            </span>
            <span className="text-[9px] text-[#5C6778]">Connect tools for live actions</span>
          </div>
        )}
        {trustData?.classification === 'needs_setup' && w.tool_preferences?.length > 0 && (
          <div className="flex items-center gap-1.5 mb-3" data-testid={`worker-limited-label-${w.id}`}>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border bg-[rgba(245,158,11,0.04)] border-[rgba(245,158,11,0.12)] text-[#F59E0B]">
              Limited
            </span>
            <span className="text-[9px] text-[#5C6778]">Some tools need connecting</span>
          </div>
        )}
        {/* BOTTOM: Metrics */}
        <div className="flex items-center justify-between pt-3.5 border-t border-[rgba(255,255,255,0.08)]">
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#7D8896]" data-testid={`worker-metric-${w.id}`}>{metric}</span>
            {lastRun && (
              <>
                <span className="text-[#2A2F38]">·</span>
                <span className="text-[11px] text-[#5C6778]">{lastRun}</span>
              </>
            )}
          </div>
          <ArrowRight size={14} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors" />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   TEAM VIEW — Visual org-chart layout
   ═══════════════════════════════════════════════ */
const TeamView = ({ workers, isOperate, navigate, userName, onDelete, trustMap }) => {
  const unique = deduplicateWorkers(workers);
  const active = unique.filter((w) => w.status === 'active');
  const draft = unique.filter((w) => w.status !== 'active');

  return (
    <div className="space-y-0" data-testid="team-view">
      {/* Business node */}
      <div className="flex justify-center mb-2">
        <div className="bg-[#252D38] border border-[rgba(255,255,255,0.08)] rounded-2xl px-6 py-4 flex items-center gap-3" data-testid="team-business-node">
          <div className="w-10 h-10 rounded-full bg-[rgba(0,229,192,0.06)] flex items-center justify-center">
            <UserCircle size={24} className="text-[#00E5C0]" weight="duotone" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#F0F4F8]">{userName || 'Your'} Team</p>
            <p className="text-[10px] text-[#7D8896]">{unique.length} AI worker{unique.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Connection line */}
      {workers.length > 0 && (
        <div className="flex justify-center">
          <div className="w-px h-8 bg-[rgba(255,255,255,0.06)]" />
        </div>
      )}

      {/* Active workers */}
      {active.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-center mb-3">
            <span className="text-[10px] font-semibold text-[#00E5C0] uppercase tracking-[0.08em] bg-[rgba(0,229,192,0.04)] px-3 py-1 rounded-full border border-[rgba(0,229,192,0.08)]">
              Active ({active.length})
            </span>
          </div>
          {/* Horizontal branch lines */}
          <div className="flex justify-center">
            <div className="flex flex-wrap justify-center gap-4 max-w-[900px]">
              {active.map((w) => (
                <div key={w.id} className="flex flex-col items-center">
                  <div className="w-px h-4 bg-[rgba(0,229,192,0.15)]" />
                  <WorkerCard w={w} isOperate={isOperate} navigate={navigate} variant="team" onDelete={onDelete} trustData={trustMap[w.id]} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Draft workers */}
      {draft.length > 0 && (
        <div>
          {active.length > 0 && (
            <div className="flex justify-center mb-2">
              <div className="w-px h-4 bg-[rgba(255,255,255,0.04)]" />
            </div>
          )}
          <div className="flex justify-center mb-3">
            <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-[0.08em] bg-[rgba(255,255,255,0.02)] px-3 py-1 rounded-full border border-[rgba(255,255,255,0.08)]">
              Draft ({draft.length})
            </span>
          </div>
          <div className="flex justify-center">
            <div className="flex flex-wrap justify-center gap-4 max-w-[900px]">
              {draft.map((w) => (
                <div key={w.id} className="flex flex-col items-center">
                  <div className="w-px h-4 bg-[rgba(255,255,255,0.04)]" />
                  <WorkerCard w={w} isOperate={isOperate} navigate={navigate} variant="team" onDelete={onDelete} trustData={trustMap[w.id]} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */
export default function ServiceAutomations() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { user } = useAuth();
  const { mode } = useMode();
  const orgId = activeOrganization?.organization_id;
  const isOperate = mode === 'operate';

  const [workers, setWorkers] = useState([]);
  const [trustMap, setTrustMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'team'
  const [showNewWorkerModal, setShowNewWorkerModal] = useState(false);

  // Voice & Operator wizard state (bypass AutomationBuilder)
  const [showVoiceWizard, setShowVoiceWizard] = useState(false);
  const [voiceWizardAgentId, setVoiceWizardAgentId] = useState(null);
  const [showOperatorModal, setShowOperatorModal] = useState(false);

  const handleVoiceSelect = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: 'New Voice Agent',
            short_description: 'Voice agent for phone-based interactions',
            capability_profile: 'live_voice_s2s_agent',
            accepted_inputs: ['audio', 'text'],
            status: 'draft',
            is_active: false,
            org_id: orgId
          })
        },
        orgId
      );
      if (response.ok) {
        const data = await response.json();
        const id = data.agent_id || data.agent?.id || data.id;
        setVoiceWizardAgentId(id);
        setShowVoiceWizard(true);
      } else {
        toast.error('Failed to create voice worker');
      }
    } catch {
      toast.error('Failed to create voice worker');
    }
  };

  const handleOperatorSelect = () => {
    setShowOperatorModal(true);
  };

  const fetchWorkers = async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      const [workersRes, trustRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/workers`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/trust-audit`, {}, orgId),
      ]);
      if (workersRes.ok) {
        const data = await workersRes.json();
        const all = (data.workers || []).map(toListWorker);
        setWorkers(all);
      }
      if (trustRes.ok) {
        const trustData = await trustRes.json();
        const map = {};
        for (const item of (trustData.audit || [])) {
          map[item.worker_id] = item;
        }
        setTrustMap(map);
      }
    } catch (err) {
      console.error('Failed to fetch workers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWorkers(); }, [orgId]);

  const handleDeleteWorker = async (workerId, workerName) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/workers/${workerId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (res.ok) {
        setWorkers(prev => prev.filter(w => w.id !== workerId));
        toast.success(`${workerName} deleted`);
      } else {
        toast.error('Failed to delete worker');
      }
    } catch {
      toast.error('Error deleting worker');
    }
  };

  const filtered = useMemo(() => {
    let items = deduplicateWorkers(workers);
    // Hide "coming_soon" workers from main marketplace
    items = items.filter(w => {
      const trust = trustMap[w.id];
      return !trust || trust.classification !== 'coming_soon';
    });
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (w) => w.name?.toLowerCase().includes(q) || w.type?.toLowerCase().includes(q) || w.automation_type?.toLowerCase().includes(q)
      );
    }
    return items;
  }, [workers, search, trustMap]);

  const { core: coreWorkers, specialty: specialtyWorkers } = useMemo(
    () => groupWorkers(filtered), [filtered]
  );

  const subtitle = isOperate
    ? 'Your workers handle tasks so you can focus on your business.'
    : 'Manage your worker workflows.';
  const createLabel = 'New Worker';

  const userName = user?.name ? user.name.split(' ')[0] : null;

  return (
    <>
      <SEO title="Workers" />
      <div className="max-w-[1280px] mx-auto" data-testid="service-automations">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-[#7D8896]">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate('/systems')}
                      data-testid="orchestrations-btn"
                      className="flex items-center gap-1.5 px-3 py-2.5 text-[#B0B8C4] text-xs font-medium rounded-lg border border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.03)] hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.06)] hover:text-[#00E5C0] transition-colors"
                    >
                      <TreeStructure size={14} weight="duotone" />
                      Orchestrations
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-[#1E2836] text-[#F0F4F8] border border-[rgba(255,255,255,0.08)]">
                    Connect multiple workers into automated flows
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                onClick={() => navigate('/workers/activity')}
                data-testid="system-activity-btn"
                className="flex items-center gap-1.5 px-3 py-2.5 text-[#7D8896] text-xs font-medium rounded-lg border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)] hover:text-[#9CA3B0] transition-colors"
              >
                <Pulse size={14} />
                Activity
              </button>
              <button
                onClick={() => navigate('/workers/mappings')}
                data-testid="mapping-dashboard-btn"
                className="flex items-center gap-1.5 px-3 py-2.5 text-[#7D8896] text-xs font-medium rounded-lg border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)] hover:text-[#9CA3B0] transition-colors"
              >
                <Plugs size={14} />
                Mappings
              </button>
              <button
                onClick={() => navigate('/workers/memory')}
                data-testid="memory-inspector-btn"
                className="flex items-center gap-1.5 px-3 py-2.5 text-[#7D8896] text-xs font-medium rounded-lg border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)] hover:text-[#9CA3B0] transition-colors"
              >
                <Eye size={14} />
                Memory
              </button>
              <button
                onClick={() => setShowNewWorkerModal(true)}
                data-testid="new-worker-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors"
              >
                <Plus size={16} weight="bold" />
                {createLabel}
              </button>
              <button
                onClick={() => navigate('/workers/browse')}
                data-testid="browse-workers-btn"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.06)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.12)] hover:border-[rgba(0,229,192,0.4)] transition-colors"
              >
                <Package size={16} weight="duotone" />
                Browse Templates
              </button>
            </div>
          </div>
        </div>

        {/* Search — list view only */}
        {workers.length > 0 && view === 'list' && (
          <div className="mb-6">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7D8896]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workers..."
                data-testid="workers-search"
                className="w-full pl-11 pr-4 py-3 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0] transition-colors"
              />
            </div>
            <p className="mt-2.5 text-[11px] text-[#5C6778] leading-relaxed" data-testid="workers-helper-text">
              Workers handle tasks individually. Orchestrations connect multiple workers to run automatically.
            </p>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={28} className="animate-spin text-[#00E5C0]" />
          </div>
        ) : workers.length === 0 ? (
          /* Empty state */
          <div className="text-center py-16" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-[rgba(0,229,192,0.06)] flex items-center justify-center mx-auto mb-4">
              <Robot size={32} className="text-[#00E5C0]" weight="light" />
            </div>
            <h2 className="text-lg font-semibold text-[#F0F4F8] mb-2">
              You don't have any workers yet
            </h2>
            <p className="text-sm text-[#7D8896] max-w-md mx-auto mb-6">
              Create a worker to handle enquiries, follow up leads, or manage bookings. Try telling us what you need.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="create-with-command-empty"
                className="flex items-center gap-2 px-5 py-3 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors"
              >
                <Lightning size={16} weight="fill" />
                Create with Command
              </button>
              <button
                onClick={() => setShowNewWorkerModal(true)}
                data-testid="create-with-builder"
                className="flex items-center gap-2 px-5 py-3 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-sm font-medium rounded-lg border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
              >
                <Plus size={16} />
                Or use the builder
              </button>
            </div>
          </div>
        ) : (
          /* List view: core workers + specialty workers */
          <>
            {filtered.length === 0 && search ? (
              <div className="text-center py-16">
                <MagnifyingGlass size={28} className="text-[#5C6778] mx-auto mb-2" />
                <p className="text-sm text-[#7D8896]">No results for &ldquo;{search}&rdquo;</p>
              </div>
            ) : (
              <>
                {/* Core Workers */}
                {coreWorkers.length > 0 && (
                  <div data-testid="core-workers-section">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="workers-grid">
                      {coreWorkers.map((w) => (
                        <WorkerCard key={w.id} w={w} isOperate={isOperate} navigate={navigate} variant="list" onDelete={handleDeleteWorker} trustData={trustMap[w.id]} />
                      ))}
                    </div>
                  </div>
                )}
                {/* Specialty Workers */}
                {specialtyWorkers.length > 0 && (
                  <div className="mt-8" data-testid="specialty-workers-section">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-px flex-1 bg-[rgba(255,255,255,0.04)]" />
                      <span className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] px-2">Specialty Workers</span>
                      <div className="h-px flex-1 bg-[rgba(255,255,255,0.04)]" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="specialty-workers-grid">
                      {specialtyWorkers.map((w) => (
                        <WorkerCard key={w.id} w={w} isOperate={isOperate} navigate={navigate} variant="list" onDelete={handleDeleteWorker} trustData={trustMap[w.id]} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <NewWorkerModal
        isOpen={showNewWorkerModal}
        onClose={() => setShowNewWorkerModal(false)}
        onVoiceSelect={handleVoiceSelect}
        onOperatorSelect={handleOperatorSelect}
      />

      {/* Voice Wizard — renders on Workers page, bypasses AutomationBuilder */}
      {showVoiceWizard && voiceWizardAgentId && (
        <div data-testid="voice-wizard-overlay">
          <UniversalVoiceWizard
            agentIdProp={voiceWizardAgentId}
            isModal={true}
            onClose={() => {
              setShowVoiceWizard(false);
              setVoiceWizardAgentId(null);
              fetchWorkers();
            }}
          />
        </div>
      )}

      {/* Operator Modal — renders on Workers page, bypasses AutomationBuilder */}
      <CreateOperatorModal
        isOpen={showOperatorModal}
        onClose={() => setShowOperatorModal(false)}
        onSuccess={(newOperator) => {
          setShowOperatorModal(false);
          fetchWorkers();
        }}
        orgId={orgId}
      />
    </>
  );
}
